/**
 * Agent Access Keys API: list, create a setup code, revoke (docs/plans/agent-access-keys.md).
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

let getToken: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

export function setAgentKeysTokenGetter(getter: (forceRefresh?: boolean) => Promise<string | null>) {
  getToken = getter;
}

export type AgentKeyStatus = 'PENDING' | 'ACTIVE' | 'REVOKED';

export interface AgentKey {
  keyId: string;
  name: string;
  status: AgentKeyStatus;
  createdAt: string;
  setupExpiresAt?: string;
  redeemedAt?: string;
  expiresAt?: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

export interface SetupCode {
  setupCode: string;
  keyId: string;
  name: string;
  setupExpiresAt: string;
}

/** Served next to the site by the frontend build (tools/dashboard-mcp: npm run bundle). */
export interface AgentBundleManifest {
  version: string;
  file: string;
  sha256: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken ? await getToken() : null;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
  return body as T;
}

export async function listAgentKeys(): Promise<AgentKey[]> {
  return (await request<{ keys: AgentKey[] }>('/agent-keys')).keys;
}

export async function createSetupCode(name: string): Promise<SetupCode> {
  return request<SetupCode>('/agent-keys', { method: 'POST', body: JSON.stringify({ name }) });
}

export async function revokeAgentKey(keyId: string): Promise<void> {
  await request(`/agent-keys?keyId=${encodeURIComponent(keyId)}`, { method: 'DELETE' });
}

/** Disconnect: revoke the active key and withdraw any pending setup code together. */
export async function disconnectAgent(): Promise<void> {
  await request('/agent-keys?all=1', { method: 'DELETE' });
}

export async function getAgentBundleManifest(): Promise<AgentBundleManifest> {
  const response = await fetch('/agent/manifest.json', { cache: 'no-store' });
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.file || !body?.sha256) throw new Error('Agent setup files are not available on this site');
  return body as AgentBundleManifest;
}
